import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { CONSTANTS } from 'src/app/dataModels/constants';
import { UserService } from '../user/user.service';
import { HelperService } from '../utils/helper.service';
import { SessionService } from '../session/session.service';
import { Router } from '@angular/router';
import { AuthService } from '../authManager/auth-manager.service';
import { of } from 'rxjs';

/**
 * API Service
 * All api calls happens here
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  /**
   * base url for the app
   */
  private baseUrl = '/core/app';
  private baseMsUrl = '/core/ms';

  /**
   * httpOptions with headers             
   * Content Type is set to 'application/json'
   */
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Referrer': 'origin'
    }),
  };

  /**
   * by default set userLogged in to true, to prevent multiple session expired errors
   */
  private isUserLoggedIn = true;

  /**
   * Constructor to inject dependency
   * @param http instance of HTTPClient
   * @param userService instance of userService
   * @param helperService instance of helperService
   * @param sessionService instance of SessionService
   * @param authService instance of AuthService
   * @param router instance of router
   */
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private helperService: HelperService,
    private sessionService: SessionService,
    private authService: AuthService,
    private router: Router
  ) {
    this.authService.isLoggedIn.subscribe((value) => {
      if (value) {
        this.isUserLoggedIn = value;
      }
    });
  }

  /**
   * Call reset API
   * @param url url of the rest api
   * @param request request
   * @param appendBaseUrl indicate if baseUrl has to be appended
   */
  post(url: string, request: any, appendBaseUrl = true) {
    this.setAuthToken();
    let requestUrl = url;
    if (appendBaseUrl) {
      requestUrl = this.baseUrl + url;
    }
    return this.http.post<any>(requestUrl, request, this.httpOptions).pipe(
      map((response) => {
        this.handleResponse(response, request.action);
        return response;
      }),
      catchError((error) => {
        return of({ status: 'TRN_ERROR', message: CONSTANTS.NJIIS_ERROR });
      })
    );
  }


  msPut(url: string, request: any, appendBaseUrl = true) {
    this.setAuthToken();
    let requestUrl = url;
    if (appendBaseUrl) {
      requestUrl = this.baseMsUrl + url;
    }
    return this.http.put<any>(requestUrl, request, this.httpOptions).pipe(
      map((response) => {
        this.handleMSResponse(response, request.action);
        return response;
      }),
      catchError((error) => {
        return of({ status: 'TRN_ERROR', message: CONSTANTS.NJIIS_ERROR });
      })
    );
  }

  msPost(url: string, request: any, appendBaseUrl = true) {
    this.setAuthToken();
    let requestUrl = url;
    if (appendBaseUrl) {
      requestUrl = this.baseMsUrl + url;
    }
    return this.http.post<any>(requestUrl, request, this.httpOptions).pipe(
      map((response) => {
        this.handleMSResponse(response, request.action);
        return response;
      }),
      catchError((error) => {
        return of({ status: 'TRN_ERROR', message: CONSTANTS.NJIIS_ERROR });
      })
    );
  }
  /* implemented for MS json results which may or may not be iterable */ 
   obj_is_iterable = ((x) => (
    (Reflect.has(x, Symbol.iterator)) &&
    (typeof (x[Symbol.iterator]) === "function")
  ));

   /**
   * handle MS response, if rest API returns AUTH_TOKEN_INVALID, invalidate user session,
   * show Session Expired message and redirect user to homePage
   * else if API returns error, combine errors messages
   * @param response response received from the rest API
   * @param action action being performed
   */
   private handleMSResponse(response, action) {
    if (response.status === CONSTANTS.AUTH_TOKEN_INVALID && this.isUserLoggedIn) {
      this.isUserLoggedIn = false;
      this.authService.isLoggedIn.next(false);
     if (action !== 'LOGOUT') {
        this.helperService.showErrorMessage('Session Expired. Please login again.').subscribe(() => {
          this.sessionService.clearSession();
          this.router.navigate(['/home']);
        });
      } else{

       this.sessionService.clearSession();
       this.router.navigate(['/home']);
      }
       //response.message = 'Session Expired. Please login again.';
    } else {
      let message = '';
      if (response.message) {
        message = response.message;
        if (response.errors) {
          if (this.obj_is_iterable(response.errors)) { 
            Object.keys(response.errors).forEach((key) => {
              message += `<br/> ${response.errors[key]}`;
            });
          } else {
            message += `<br/> ${response.errors}`;
          }
        } else if (response.json) {
          if (this.obj_is_iterable(response.json)) { 
          for (const obj of response.json) {
            if (obj.status !== CONSTANTS.SUCCESS) {
              message += `<br/>${obj.message}`;
            }
          }
        } 
        } else if (response.objects) {
          if (this.obj_is_iterable(response.objects)) { 
          for (const obj of response.objects) {
            if (obj.status !== CONSTANTS.SUCCESS) {
              message += `<br/>${obj.message}`;
            }
          }
         }
        }
        response.message = message;
      } else {
        response.message = CONSTANTS.NJIIS_ERROR;
      }
    }
  }

  /**
   * handle response, if rest API returns AUTH_TOKEN_INVALID, invalidate user session,
   * show Session Expired message and redirect user to homePage
   * else if API returns error, combine errors messages
   * @param response response received from the rest API
   * @param action action being performed
   */
  private handleResponse(response, action) {
    if (response.status === CONSTANTS.AUTH_TOKEN_INVALID && this.isUserLoggedIn) {
      this.isUserLoggedIn = false;
      this.authService.isLoggedIn.next(false);
      if (action !== 'LOGOUT') {
        this.helperService.showErrorMessage('Session Expired. Please login again.').subscribe(() => {
          this.sessionService.clearSession();
          this.router.navigate(['/home']);
        });
      }
      response.message = 'Session Expired. Please login again.';
    } else {
      let message = '';
      if (response.message) {
        message = response.message;
        if (response.errors) {
          Object.keys(response.errors).forEach((key) => {
            message += `<br/> ${response.errors[key]}`;
          });
        } else if (response.objects) {
          for (const obj of response.objects) {
            if (obj.status !== CONSTANTS.SUCCESS) {
              message += `<br/>${obj.message}`;
            }
          }
        }
        response.message = message;
      } else {
        response.message = CONSTANTS.NJIIS_ERROR;
      }
    }
  }

  /**
   * Add authToken to the HttpHeaders
   */
  setAuthToken() {
    let isAuthTokenAvailable = false;
    const user = this.userService.getUser();
    if (!this.helperService.IsNullOrEmpty(user) && !this.helperService.IsNullOrEmpty(user.authToken)) {
      isAuthTokenAvailable = true;
      this.httpOptions.headers = this.httpOptions.headers.set(CONSTANTS.AUTH_TOKEN, user.authToken);
    }
    else {
      this.httpOptions.headers = this.httpOptions.headers.delete(CONSTANTS.AUTH_TOKEN);
    }

    return isAuthTokenAvailable;
  }

  /**
   * Call reset API
   * @param url url of the rest api
   * @param request request
   */
  downloadFile(url: string, request: any) {
    const isAuthTokenAvailable = this.setAuthToken();
    return this.http.post<any>(this.baseUrl + url, request, { headers: this.httpOptions.headers, responseType: 'blob' as 'json' }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        return CONSTANTS.NJIIS_ERROR;
      })
    );
  }

  /**
   * Call reset API
   * @param url url of the rest api
   * @param request request
   */
  msDownloadFile(url: string, request: any) {
    const isAuthTokenAvailable = this.setAuthToken();
    return this.http.post<any>(this.baseMsUrl + url, request, { headers: this.httpOptions.headers, responseType: 'blob' as 'json' }).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        return CONSTANTS.NJIIS_ERROR;
      })
    );
  }

}



